<template>
  <div class="festival-ops">
    <festival-ops-nav active-path="/festival/products" />
    <section class="festival-ops__panel">
      <div class="festival-ops__head">
        <div>
          <h2 class="festival-ops__title">节日商品库</h2>
          <p class="festival-ops__muted">商品资料、库存和订单均由节日专区独立维护。</p>
        </div>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openEditor()">新增商品</el-button>
      </div>
      <div class="festival-ops__filter">
        <el-input v-model="filter.title" clearable placeholder="搜索商品名称" style="width:220px" @keyup.enter.native="search" />
        <el-select v-model="filter.enabled" clearable placeholder="全部状态" style="width:130px">
          <el-option label="已上架" value="Y" />
          <el-option label="已下架" value="N" />
        </el-select>
        <el-button icon="el-icon-search" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>
    </section>

    <section class="festival-ops__panel">
      <el-table v-loading="loading" :data="rows" border>
        <el-table-column label="图片" width="80">
          <template slot-scope="scope">
            <el-image :src="imageUrl(scope.row.imageUrl)" fit="cover" style="width:48px;height:48px;border-radius:8px" />
          </template>
        </el-table-column>
        <el-table-column prop="title" label="商品名称" min-width="160" />
        <el-table-column label="商品类型" width="120">
          <template slot-scope="scope">{{ businessLabel(scope.row.actionType) }}</template>
        </el-table-column>
        <el-table-column prop="badge" label="角标" width="100" />
        <el-table-column label="活动价" width="90">
          <template slot-scope="scope"><span class="festival-ops__price">¥{{ scope.row.salePrice }}</span></template>
        </el-table-column>
        <el-table-column prop="stock" label="库存" width="80" />
        <el-table-column label="可购买" width="90">
          <template slot-scope="scope">
            <el-tag :type="isJumpableProduct(scope.row) ? 'success' : 'info'" size="mini">
              {{ isJumpableProduct(scope.row) ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="scope">
            <el-tag :type="scope.row.enabled === 'Y' ? 'success' : 'info'" size="mini">
              {{ scope.row.enabled === 'Y' ? '上架' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="openEditor(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-link" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="festival-ops__pagination"
        :current-page.sync="page.current"
        :page-size="page.size"
        :total="page.total"
        layout="total, prev, pager, next"
        @current-change="load"
      />
    </section>

    <el-dialog :title="form.productId ? '编辑节日商品' : '新增节日商品'" :visible.sync="dialog" width="720px">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <div class="form-grid">
          <el-form-item label="商品名称" prop="title"><el-input v-model="form.title" maxlength="40" /></el-form-item>
          <el-form-item label="运营分类ID"><el-input v-model="form.categoryId" maxlength="40" placeholder="如 mid-autumn-gift" /></el-form-item>
          <el-form-item label="商品卖点"><el-input v-model="form.subtitle" maxlength="50" /></el-form-item>
          <el-form-item label="角标"><el-input v-model="form.badge" maxlength="8" placeholder="限时折扣" /></el-form-item>
          <el-form-item label="活动价" prop="salePrice"><el-input v-model="form.salePrice" /></el-form-item>
          <el-form-item label="原价"><el-input v-model="form.originalPrice" /></el-form-item>
          <el-form-item label="展示库存"><el-input-number v-model="form.stock" :min="0" style="width:100%" /></el-form-item>
          <el-form-item label="排序"><el-input-number v-model="form.sort" :min="0" style="width:100%" /></el-form-item>
          <el-form-item label="销售单位"><el-input v-model="form.unit" maxlength="10" placeholder="份 / 盒 / 套" /></el-form-item>
          <el-form-item label="商品类型"><el-input value="节日专区自营" disabled /></el-form-item>
        </div>
        <el-alert :title="businessHint(form.actionType)" type="info" :closable="false" show-icon style="margin-bottom:16px" />
        <el-form-item label="商品详情"><el-input v-model="form.detail" type="textarea" :rows="4" maxlength="1000" show-word-limit placeholder="介绍规格、包装、配送与售后说明" /></el-form-item>
        <el-form-item label="商品主图" prop="imageUrl">
          <product-image-field v-model="form.imageUrl" platform="TAOBAO_FLASH" @uploading-change="imageUploading = $event" />
        </el-form-item>
        <el-form-item label="上架状态">
          <el-switch v-model="form.enabled" active-text="上架" inactive-text="下架" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" :disabled="imageUploading" @click="save">保存商品</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import ProductImageField from '@/components/upload/ProductImageField.vue'
import { deleteFestivalProduct, listFestivalProducts, saveFestivalProduct } from '@/api/life/festivalApi'
import { resolveImageUrl } from '@/utils/image-url'
import FestivalOpsNav from './festivalNav.vue'
import {
  businessHint,
  businessLabel,
  emptyProduct,
  isJumpableProduct,
  parseProductForm
} from './festivalConfig'
import './festival-ops.scss'

export default {
  name: 'FestivalProducts',
  components: { FestivalOpsNav, ProductImageField },
  data() {
    return {
      loading: false,
      saving: false,
      dialog: false,
      imageUploading: false,
      filter: { title: '', enabled: '' },
      page: { current: 1, size: 20, total: 0 },
      rows: [],
      form: emptyProduct(),
      rules: {
        title: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        imageUrl: [{ required: true, message: '请上传商品图片', trigger: 'change' }],
        salePrice: [{ required: true, message: '请输入活动价', trigger: 'blur' }]
      }
    }
  },
  created() { this.load() },
  methods: {
    imageUrl: resolveImageUrl,
    businessLabel,
    businessHint,
    isJumpableProduct,
    search() { this.page.current = 1; this.load() },
    reset() { this.filter = { title: '', enabled: '' }; this.search() },
    async load() {
      this.loading = true
      try {
        const res = await listFestivalProducts({
          title: this.filter.title,
          enabled: this.filter.enabled,
          page: this.page.current,
          row: this.page.size
        })
        this.rows = res.data || []
        this.page.total = Number(res.total || 0)
      } catch (error) {
        this.$message.error(error.message || '加载商品库失败')
      } finally {
        this.loading = false
      }
    },
    openEditor(row) {
      this.form = parseProductForm(row)
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    save() {
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          await saveFestivalProduct(this.form)
          this.$message.success('商品已保存')
          this.dialog = false
          await this.load()
        } catch (error) {
          this.$message.error(error.message || '保存商品失败')
        } finally {
          this.saving = false
        }
      })
    },
    async remove(row) {
      try {
        await this.$confirm(`确认删除“${row.title}”？`, '删除商品', { type: 'warning' })
        await deleteFestivalProduct({ productId: row.productId })
        this.$message.success('商品已删除')
        this.load()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') this.$message.error(error.message || '删除失败')
      }
    }
  }
}
</script>

<style scoped>
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 18px; }
.form-grid ::v-deep .el-form-item { margin-bottom: 16px; }
.danger-link { color: #e5574f !important; }
@media (max-width: 720px) { .form-grid { grid-template-columns: 1fr; } }
</style>
